import type { Call } from '@entities/call';
import type { UserPhoneNumber } from '@entities/user';
import type { BaseUsecase } from '@usecases/base-usecase';

export const MANDATORY_TAGGING_ERROR =
  'Mandatory tagging required: at least one tag must be applied before ending wrap-up';

export interface WrapUpTimeEndedEventPublisher {
  publish(): Promise<void>;
}

export interface WrapUpTimeEndedCallRepository {
  getCurrentCall(): Promise<Call>;
}

export interface WrapUpTimeEndedUserRepository {
  getOutboundUserPhoneNumber(): Promise<UserPhoneNumber>;
}

export interface WrapUpTimeEndedTagRepository {
  getAppliedTags(callId: string): number[];
}

export interface WrapUpTimeEndedCallUsecase extends BaseUsecase<void> {
  run(): Promise<void>;
}

export class WrapUpTimeEndedUseCase implements WrapUpTimeEndedCallUsecase {
  constructor(
    private publisher: WrapUpTimeEndedEventPublisher,
    private callRepository?: WrapUpTimeEndedCallRepository,
    private userRepository?: WrapUpTimeEndedUserRepository,
    private tagRepository?: WrapUpTimeEndedTagRepository,
  ) {}

  async run(): Promise<void> {
    if (await this.shouldBlockForMandatoryTagging()) {
      throw new Error(MANDATORY_TAGGING_ERROR);
    }

    await this.publisher.publish();
  }

  // Returns true only when tagging is mandatory for the agent's number and no
  // tags have been applied to the current call. Any lookup error fails open
  // (returns false) so a transient outage can't trap the agent in wrap-up.
  private async shouldBlockForMandatoryTagging(): Promise<boolean> {
    if (!this.callRepository || !this.userRepository || !this.tagRepository) {
      return false;
    }

    try {
      const call = await this.callRepository.getCurrentCall();
      if (!call) return false;

      const number = await this.userRepository.getOutboundUserPhoneNumber();
      if (!number?.mandatory_tagging_activated) return false;

      const applied = this.tagRepository.getAppliedTags(call.id);
      return applied.length === 0;
    } catch {
      return false;
    }
  }
}
